import { type MeObject, useCentralizedProviderData } from '@crehana/auth';
import MenuCollapse from '@crehana/compass.icons.outline/MenuCollapse';
import { LayoutWithNavbarAndSidebar } from '@crehana/compass.layouts/layout-with-navbar-and-sidebar';
import { PageLoader } from '@crehana/compass.ui/v2';
import { useFeatureFlag } from '@crehana/feature-flags/react';
import {
  MenuItem,
  NewDashboardMenu,
  NewRouteWrapper,
  type SingleRouteType,
  useMe,
  useUITheme,
} from '@crehana/web';
import { useRouter } from 'next/router';

import { FEATURE_FLAGS_V5 } from '@/shared/components/AdmindashboardView/featureFlags';
import useFlagOrganization from '@/views/AdminDashboard/hooks/useFlagOrganization';
import useLeadership from '@/views/AdminDashboard/hooks/useLeadership';

import TeamSelect from './components/TeamSelect';
import useNewRoutes from './hooks/useNewRoutes';
import useOrgLogo from './hooks/useOrgLogo';
import { LogoWrapper, SidebarWrapper } from './SidebarMenu.styles';
import type { SidebarMenuProps } from './types';

const SidebarMenu: React.FC<React.PropsWithChildren<SidebarMenuProps>> = ({
  routes,
  secondaryRoutes,
  slug,
  isDark = true,
  baseUpperRoutes,
  hasAdminModules,
  children,
}) => {
  const { me } = useMe();
  const { push } = useRouter();
  const { toggleTheme } = useUITheme();
  const { isLeaderPanel, hiddenTeamSelect } = useLeadership();

  const {
    mappedRoutes,
    mappedBottomRoutes,
    upperRoutes,
    currentSingleRoute,
    selectPageOrOpenLink,
    onClickItem,
  } = useNewRoutes(routes, secondaryRoutes, baseUpperRoutes);

  const { loading, currentPageOrganization } = useCentralizedProviderData();

  const { flagValue: isSidebarMenuEnabled, loading: loadingSidebarMenu } =
    useFeatureFlag(FEATURE_FLAGS_V5.NEW_SIDEBAR_LAYOUT);

  const talentSuitFlag = useFlagOrganization({
    flag: 'talent-suite',
  });

  const canRenderProductDropdown =
    talentSuitFlag.isActive &&
    !talentSuitFlag.isLoading &&
    !isLeaderPanel &&
    hasAdminModules;

  const { organizationLogo: orgLogo } = useOrgLogo(slug, isDark);

  const logo =
    isLeaderPanel && !hiddenTeamSelect
      ? {
          ...orgLogo,
          expanded: (
            <LogoWrapper className="flex flex-col w-full">
              {orgLogo?.expanded}
              <TeamSelect isDark={isDark} />
            </LogoWrapper>
          ),
        }
      : orgLogo;

  // biome-ignore lint/style/useBlockStatements: single-line early return
  if (
    !mappedRoutes ||
    /*
      This condition is important to do not mess with the Tour component
      that needs to SidebarMenu to be fully rendered before search inside it
      for data-tour selectors.
     */
    talentSuitFlag.isLoading
  )
    return <></>;

  const homeUrl = isLeaderPanel
    ? `/org/${slug}/panel/lider/`
    : `/org/${slug}/panel/`;

  if (loadingSidebarMenu || loading) {
    return (
      <div className="flex items-center justify-center w-full min-h-screen bg-base-lighter-8">
        <PageLoader size="s" />
      </div>
    );
  }

  if (isSidebarMenuEnabled && currentPageOrganization) {
    return (
      <LayoutWithNavbarAndSidebar
        organizationSlug={currentPageOrganization.slug}
        centralizedOrganizationId={currentPageOrganization.id}
        viewMode="admin"
        handledPathnames={[]}
        onHandledUrlClick={push}
      >
        {children}
      </LayoutWithNavbarAndSidebar>
    );
  }

  return (
    <SidebarWrapper
      id={'sidebar-b2b'}
      isDark={isDark}
      heightAuto={isLeaderPanel}
      supportsDarkMode
      user={me as MeObject}
      toggleTheme={toggleTheme}
      initialRoute={currentSingleRoute}
      source={'NEW_ADMIN_DASHBOARD_MENU'}
      onChangeRoute={route => selectPageOrOpenLink(route)}
    >
      <NewDashboardMenu
        customHomeUrl={homeUrl}
        organizationLogo={logo}
        organizationSlug={slug}
        iconCollapse={MenuCollapse}
        canRenderProductDropdown={canRenderProductDropdown}
        /**
         * Custom onClickItem to avoid dependencies with next or react-router
         * each team can use this method and redirect with the correct navigation
         */
        onClickItem={onClickItem}
        renderUpperLinks={{
          itemLinks: () =>
            upperRoutes.map(route => {
              const _route = route as SingleRouteType;
              // We are adding this source to track the source of the Home Panel render event
              const pathWithSource = _route.path + '?source_page=Sidebar';

              return (
                <MenuItem
                  key={route.key}
                  route={{
                    ..._route,
                    path: pathWithSource,
                  }}
                  isDark={isDark}
                />
              );
            }),
        }}
        renderLinks={{
          itemLinks: () =>
            mappedRoutes.map(route => (
              <MenuItem key={route.key} route={route} isDark={isDark} />
            )),
        }}
        renderBottomLinks={{
          itemLinks: () =>
            mappedBottomRoutes.map(route => (
              <MenuItem
                key={route.key}
                route={route}
                isDark={isDark}
                isBottomLink
              />
            )),
        }}
        isProduction={process.env.APP_ENV === 'production'}
      />
      <NewRouteWrapper>{children}</NewRouteWrapper>
    </SidebarWrapper>
  );
};

export default SidebarMenu;
